import { createRouter, createWebHistory } from 'vue-router'
import { useStats } from '@/stores/stats'
import { useSession } from '@/stores/session'
import { useConnections } from '@/stores/connections'
import { getHerokuAuth } from '@/api/auth'
import { SignJWT } from 'jose'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      redirect: '/dashboard',
    },
    {
      path: '/issues',
      name: 'admin-data-issues',
      component: () => import('../views/AdminDataIssues.vue'),
      meta: {
        requiresDataIssues: true,
        breadcrumbs: [
          {
            title: 'Home',
            to: { name: 'admin-home' },
            disabled: false,
          },
          { title: 'Data Issues', disabled: true },
        ],
      },
    },
    {
      path: '/',
      component: () => import('../views/AdminRoot.vue'),
      children: [
        {
          path: 'dashboard',
          name: 'admin-home',
          component: () => import('../views/AdminHome.vue'),
          meta: {
            title: 'Home',
          },
        },
        {
          path: 'duplicate-sets',
          name: 'admin-duplicate-sets',
          component: () => import('../views/AdminDuplicateSets.vue'),
          meta: {
            isSideBarHidden: true,
            breadcrumbs: [
              {
                title: 'Home',
                to: { name: 'admin-home' },
                disabled: false,
              },
              { title: 'Duplicate Sets', disabled: true },
            ],
          },
        },
        {
          path: 'audit-log',
          children: [
            {
              path: '',
              name: 'admin-audit-log',
              component: () => import('../views/AdminAuditLog.vue'),
              meta: {
                title: 'Audit Log',
              },
            },
            {
              path: 'merged',
              name: 'admin-audit-log-merged',
              component: () => import('../views/AdminAuditLogMerged.vue'),
              meta: {
                title: 'Merged Duplicate Sets',
                isSideBarHidden: true,
                breadcrumbs: [
                  {
                    title: 'Audit Log',
                    to: { name: 'admin-audit-log' },
                    disabled: false,
                  },
                  { title: 'Merged Duplicate Sets', disabled: true },
                ],
              },
            },
            {
              path: 'queued',
              name: 'admin-audit-log-queued',
              component: () => import('../views/AdminAuditLogQueued.vue'),
              meta: {
                title: 'Queued Duplicate Sets',
                isSideBarHidden: true,
                breadcrumbs: [
                  {
                    title: 'Audit Log',
                    to: { name: 'admin-audit-log' },
                    disabled: false,
                  },
                  { title: 'Queued Duplicate Sets', disabled: true },
                ],
              },
            },
            {
              path: 'queued-records',
              name: 'admin-audit-log-queued-records',
              component: () =>
                import('../views/AdminAuditLogQueuedRecords.vue'),
              meta: {
                requiresDataIssues: true,
                title: 'Queued Records',
                isSideBarHidden: true,
                breadcrumbs: [
                  {
                    title: 'Audit Log',
                    to: { name: 'admin-audit-log' },
                    disabled: false,
                  },
                  { title: 'Queued Records', disabled: true },
                ],
              },
            },
            {
              path: 'undo-merge-errors',
              name: 'admin-audit-log-undo-merge-errors',
              component: () =>
                import('../views/AdminAuditLogUndoMergeErrors.vue'),
              meta: {
                title: 'Undo Merge Errors List',
                isSideBarHidden: true,
                breadcrumbs: [
                  {
                    title: 'Audit Log',
                    to: { name: 'admin-audit-log' },
                    disabled: false,
                  },
                  { title: 'Undo Merge Errors List', disabled: true },
                ],
              },
            },
            {
              path: 'merge-errors',
              name: 'admin-audit-log-merge-errors',
              component: () => import('../views/AdminAuditLogMergeErrors.vue'),
              meta: {
                title: 'Merge Errors List',
                isSideBarHidden: true,
                breadcrumbs: [
                  {
                    title: 'Audit Log',
                    to: { name: 'admin-audit-log' },
                    disabled: false,
                  },
                  { title: 'Merge Errors List', disabled: true },
                ],
              },
            },
            {
              path: 'fixed',
              name: 'admin-audit-log-fixed',
              component: () => import('../views/AdminAuditLogFixed.vue'),
              meta: {
                requiresDataIssues: true,
                title: 'Fixed Records',
                isSideBarHidden: true,
                breadcrumbs: [
                  {
                    title: 'Audit Log',
                    to: { name: 'admin-audit-log' },
                    disabled: false,
                  },
                  { title: 'Fixed Records', disabled: true },
                ],
              },
            },
            {
              path: 'fix-ignored',
              name: 'admin-audit-log-fix-ignored',
              component: () => import('../views/AdminAuditLogFixIgnored.vue'),
              meta: {
                requiresDataIssues: true,
                title: 'Fix Ignored List',
                isSideBarHidden: true,
                breadcrumbs: [
                  {
                    title: 'Audit Log',
                    to: { name: 'admin-audit-log' },
                    disabled: false,
                  },
                  { title: 'Fix Ignored List', disabled: true },
                ],
              },
            },
            {
              path: 'fix-errors',
              name: 'admin-audit-log-fix-errors',
              component: () => import('../views/AdminAuditLogFixErrors.vue'),
              meta: {
                requiresDataIssues: true,
                title: 'Fix Errors List',
                isSideBarHidden: true,
                breadcrumbs: [
                  {
                    title: 'Audit Log',
                    to: { name: 'admin-audit-log' },
                    disabled: false,
                  },
                  { title: 'Fix Errors List', disabled: true },
                ],
              },
            },
            {
              path: 'undo-fix-errors',
              name: 'admin-audit-log-undo-fix-errors',
              component: () =>
                import('../views/AdminAuditLogUndoFixErrors.vue'),
              meta: {
                requiresDataIssues: true,
                title: 'Undo Fix Errors',
                isSideBarHidden: true,
                breadcrumbs: [
                  {
                    title: 'Audit Log',
                    to: { name: 'admin-audit-log' },
                    disabled: false,
                  },
                  { title: 'Undo Fix Errors', disabled: true },
                ],
              },
            },
          ],
        },
        {
          path: 'detect-and-merge',
          children: [
            {
              path: 'search-merge',
              name: 'admin-detect-and-merge-search-merge',
              component: () =>
                import('../views/AdminDetectAndMergeSearchMerge.vue'),
              meta: {
                title: 'Search & Merge',
                requiresSearchMerge: true,
                breadcrumbs: [
                  {
                    title: 'Detect & Merge',
                    disabled: false,
                  },
                  { title: 'Search & Merge', disabled: true },
                ],
              },
            },
            {
              path: 'layouts',
              children: [
                {
                  path: '',
                  name: 'admin-detect-and-merge-layouts',
                  component: () =>
                    import('../views/AdminDetectAndMergeLayouts.vue'),
                  meta: {
                    title: 'Layouts',
                    breadcrumbs: [
                      {
                        title: 'Detect & Merge',
                        disabled: false,
                      },
                      { title: 'Layouts', disabled: true },
                    ],
                  },
                },
                {
                  path: ':layoutId',
                  name: 'admin-detect-and-merge-layouts-details',
                  component: () =>
                    import('../views/AdminDetectAndMergeLayoutsDetails.vue'),
                  meta: {
                    isSideBarHidden: true,
                  },
                },
              ],
            },
            {
              path: 'business-rules',
              children: [
                {
                  path: '',
                  name: 'admin-detect-and-merge-business-rules',
                  component: () =>
                    import('../views/AdminDetectAndMergeBusinessRules.vue'),
                  meta: {
                    title: 'Business Rules',
                    breadcrumbs: [
                      {
                        title: 'Detect & Merge',
                        disabled: false,
                      },
                      { title: 'Business Rules', disabled: true },
                    ],
                  },
                },
                {
                  path: ':id',
                  name: 'admin-detect-and-merge-business-rules-details',
                  component: () =>
                    import(
                      '../views/AdminDetectAndMergeBusinessRulesDetails.vue'
                    ),
                  meta: {
                    isSideBarHidden: true,
                    breadcrumbs: [
                      {
                        title: 'Detect & Merge',
                        disabled: false,
                      },
                      {
                        title: 'Business Rules',
                        to: { name: 'admin-detect-and-merge-business-rules' },
                        disabled: false,
                      },
                      { title: 'Details', disabled: true },
                    ],
                  },
                },
              ],
            },
          ],
        },
        {
          path: 'detect-and-fix',
          children: [
            {
              path: 'rules',
              children: [
                {
                  path: '',
                  name: 'admin-data-dimensions-rules',
                  component: () =>
                    import('../views/AdminDetectAndFixRules.vue'),
                  meta: {
                    requiresDataIssues: true,
                    title: 'Data Issue Rules',
                    breadcrumbs: [
                      {
                        title: 'Data Dimensions',
                        disabled: false,
                      },
                      { title: 'Data Issues Rules', disabled: true },
                    ],
                  },
                },
                {
                  path: ':directorId',
                  name: 'admin-detect-and-fix-objects-rules',
                  component: () =>
                    import('../views/AdminDetectAndFixRulesDetails.vue'),
                  meta: {
                    requiresDataIssues: true,
                    isSideBarHidden: true,
                    breadcrumbs: [
                      {
                        title: 'Detect & Fix',
                        disabled: false,
                      },
                      { title: 'Rules', disabled: true },
                    ],
                  },
                },
              ],
            },
          ],
        },
        {
          path: 'search-before-create',
          children: [
            {
              path: 'layouts',
              name: 'admin-search-before-create-layouts',
              component: () =>
                import('../views/AdminSearchBeforeCreateLayouts.vue'),
              meta: {
                title: 'Layouts',
                breadcrumbs: [
                  {
                    title: 'Search Before Create',
                    disabled: false,
                  },
                  { title: 'Layouts', disabled: true },
                ],
              },
            },
            {
              path: 'business-rules',
              children: [
                {
                  path: '',
                  name: 'admin-search-before-create-business-rules',
                  component: () =>
                    import('../views/AdminSearchBeforeCreateBusinessRules.vue'),
                  meta: {
                    title: 'Business Rules',
                    breadcrumbs: [
                      {
                        title: 'Search Before Create',
                        disabled: false,
                      },
                      { title: 'Business Rules', disabled: true },
                    ],
                  },
                },
                {
                  path: ':id',
                  name: 'admin-search-before-create-business-rules-details',
                  component: () =>
                    import(
                      '../views/AdminSearchBeforeCreateBusinessRulesDetails.vue'
                    ),
                  meta: {
                    isSideBarHidden: true,
                    breadcrumbs: [
                      {
                        title: 'Search Before Create',
                        disabled: false,
                      },
                      {
                        title: 'Business Rules',
                        to: {
                          name: 'admin-search-before-create-business-rules',
                        },
                        disabled: false,
                      },
                      { title: 'Details', disabled: true },
                    ],
                  },
                },
              ],
            },
          ],
        },
        {
          path: 'processes',
          name: 'admin-processes',
          component: () => import('../views/AdminProcesses.vue'),
          meta: {
            title: 'Processes',
          },
        },
        {
          path: '/general-settings',
          name: 'admin-general-settings',
          component: () => import('../views/AdminGeneralSettingsSetup.vue'),
        },
      ],
    },
    {
      path: '/general-settings/heroku',
      name: 'admin-general-settings-setup',
      component: () => import('../views/AdminGeneralSettingsSetup.vue'),
    },
    {
      path: '/find-duplicates',
      name: 'find-duplicates',
      component: () => import('../views/FindDuplicates.vue'),
    },
    {
      path: '/record-audit-trail',
      name: 'record-audit-trail',
      component: () => import('../views/RecordAuditTrail.vue'),
    },
    {
      path: '/sbc',
      name: 'search-before-create',
      component: () => import('../views/SearchBeforeCreate.vue'),
      meta: {
        isLightweightRoute: true,
      },
    },
  ],
})

// Lightweight initialization for SBC widget
async function initializeSbcRoute(sessionStore) {
  const contextMessage = JSON.parse(localStorage.getItem('contextMessage'))

  if (!contextMessage) {
    return
  }

  const token = await generateJwt(contextMessage)
  sessionStore.setJWT(token)
  sessionStore.setContextMessage(contextMessage)

  // Only fetch minimal user data (systemStatus is what SBC needs)
  await sessionStore.getUser()
}

// Full initialization for admin routes
async function initializeAdminRoute(
  sessionStore,
  statsStore,
  connectionsStore,
  to,
) {
  const contextMessage = JSON.parse(localStorage.getItem('contextMessage'))

  if (!contextMessage) {
    return
  }

  const token = await generateJwt(contextMessage)
  sessionStore.setJWT(token)
  sessionStore.setContextMessage(contextMessage)

  try {
    if (
      sessionStore.user &&
      !connectionsStore.isSomeConnectionDisconnected &&
      !connectionsStore.isSomeConnectionMissing
    ) {
      //Re-check flag
      if (to.meta.requiresDataIssues || to.meta.requiresSearchMerge) {
        await sessionStore.getUser()
      } else {
        sessionStore.getUser()
      }
    } else {
      await sessionStore.getUser()
    }

    // CDSF: redirect to wizard if setup not finished (must be before
    // connections check — fresh installs have missing SF connection)
    if (
      sessionStore.stepper &&
      !sessionStore.stepper.isFinished &&
      !sessionStore.stepper.isHerokuRequired
    ) {
      return { name: 'admin-general-settings-setup' }
    }

    if (
      !connectionsStore.isSomeConnectionDisconnected &&
      !connectionsStore.isSomeConnectionMissing
    ) {
      // General Settings fetches its own metadata (see
      // AdminGeneralSettingsSetup), because in-app navigation to it doesn't
      // re-run this guard. Fetching here too would just duplicate that call.
      if (to.name !== 'admin-general-settings') {
        if (
          !sessionStore.fieldsPerObject ||
          !sessionStore.fieldsPerMultiMap ||
          !sessionStore.operators
        ) {
          await sessionStore.getSystemMetadata()
        } else {
          sessionStore.getSystemMetadata()
        }
      }

      await statsStore.getMetadata()

      if (to.name === 'admin-home') {
        sessionStore.getRolesList()
      }

      if (sessionStore.stepper.isHerokuRequired) {
        connectionsStore.getConnectionObjects(
          connectionsStore.herokuConnection.id,
        )
      } else {
        connectionsStore.getConnectionObjects(
          connectionsStore.salesforceConnection.id,
        )
      }
    }
  } catch {
    console.log('router error')
  }

  return null
}

router.beforeEach(async (to, from) => {
  const sessionStore = useSession()
  const statsStore = useStats()
  const connectionsStore = useConnections()

  if (!from.name) {
    sessionStore.isStartingPageLoading = true

    if (to.name === 'admin-general-settings-setup') {
      const code = to.query.heroku_auth_token

      if (code) {
        const newQueryParams = { ...to.query }
        delete newQueryParams.heroku_auth_token

        const herokuToken = await getHerokuAuth(code)
        sessionStore.setHerokuToken(herokuToken)

        return { path: to.path, query: newQueryParams }
      } else if (import.meta.env.VITE_IS_STANDALONE === 'true') {
        sessionStore.setHerokuToken(localStorage.getItem('apiKey'))
      } else if (!sessionStore.jwt) {
        // CDSF: initialize JWT from Salesforce context (handles page refresh)
        const contextMessage = JSON.parse(
          localStorage.getItem('contextMessage'),
        )
        if (contextMessage) {
          const token = await generateJwt(contextMessage)
          sessionStore.setJWT(token)
          sessionStore.setContextMessage(contextMessage)
        }
      }
    } else if (to.meta.isLightweightRoute) {
      // Lightweight initialization for SBC and similar widgets
      await initializeSbcRoute(sessionStore)
    } else {
      // Full initialization for admin routes
      const redirect = await initializeAdminRoute(
        sessionStore,
        statsStore,
        connectionsStore,
        to,
      )
      if (redirect) {
        return redirect
      }
    }
  }

  // Data Issues license gate: deep links to Data-Issues-only screens fall
  // back to the dashboard for orgs that are not entitled to them
  if (to.meta.requiresDataIssues && !sessionStore.isDataIssuesEnabled) {
    return { name: 'admin-home' }
  }

  // Search & Merge flag gate: hiding the nav entry is not enough, the URL has
  // to fail too.
  if (to.meta.requiresSearchMerge && !sessionStore.canSearchMerge) {
    return { name: 'admin-home' }
  }
})

router.afterEach(() => {
  const sessionStore = useSession()

  sessionStore.isStartingPageLoading = false
})

async function generateJwt(message) {
  const payload = {
    org_id: message.orgId,
    session_id: message.sessionId,
    permission_name: message.permissionName,
    user_id: message.userId,
    user_email: message.email,
    user_first_name: message.firstName,
    user_last_name: message.lastName,
    user_name: message.userName,
    totp: message.totp,
    data: {
      entity_key: message.data.entityKey,
      entity_name: message.data.entityName,
      record_type_id: message.data.recordTypeId,
    },
  }

  const secret = new TextEncoder().encode(import.meta.env.VITE_APP_JWS_SECRET)

  return await new SignJWT(payload)
    .setProtectedHeader({ alg: 'HS256' })
    .setIssuedAt()
    .sign(secret)
}

export default router
